import React from "react"
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil"
import { useModalProvider } from "../../../Providers/ModalProvider"
import { SD_DETECTION_WINDOW_TIME_PRESETS } from "../Viewport/Components/XAxis"

import { editedLayoutGroupAtom, selectedLayoutAtom, selectedLayoutGroupAtom, uneditedLayoutGroupAtom } from "../../../Pages/Data/Visualize/DataReview/Atoms/Layout"
import { SdDetectionConfigAtom } from "../../../Pages/Data/Visualize/DataReview/Atoms/SdDetection"
import { ConfigureSDDetectionModal } from "../../../Pages/Data/Visualize/DataReview/Components/Modals/ConfigureSdDetectionModal"
import { VisualizationToolbar } from "../../../Pages/Data/Visualize/DataReview/Components/React/VisualizationToolbar"
import { DataReviewEEGMontageDropdown } from "../../../Components/EEGMontageDropdownMenu/DataReviewEEGMontageDropdown"
import { MobergDropdown } from "../../../Moberg"
import { buildModalityTargetsForEEG  } from "../../../Pages/Data/Visualize/DataReview/Utils/modalityJumpTargets"
import { useModalityJumpTargets } from "../../../Pages/Data/Visualize/DataReview/Hooks/useModalityJumpTargets"
import { useEnvironmentVariablesProvider } from "../../../Providers/EnvironmentVariablesProvider"
import { EEGMontage } from "../../../Pages/Data/Visualize/DataReview/Types/EEG"

type SDDetectionWindowSettingsProps = {
	windowId: string
	// The window's montage, which can come from the saved props before the window initializes
	montage: EEGMontage | null
}

export function SDDetectionWindowSettings(props: SDDetectionWindowSettingsProps) {
	const { createModal } = useModalProvider()
	const setUneditedLayoutGroup = useSetRecoilState(uneditedLayoutGroupAtom)
	const setEditedLayoutGroup = useSetRecoilState(editedLayoutGroupAtom)
	const selectedLayoutGroup = useRecoilValue(selectedLayoutGroupAtom)
	const selectedLayout = useRecoilValue(selectedLayoutAtom)
	const { environmentVariables } = useEnvironmentVariablesProvider()

	const atom = SdDetectionConfigAtom({ layoutId: selectedLayout?.id as string, windowId: props.windowId })
	const [sdConfig, setSDConfig] = useRecoilState(atom)
	const [viewStart, viewEnd] = sdConfig.viewScale.domain()
	const viewDuration = viewEnd.getTime() - viewStart.getTime()

	const [fileStart, fileEnd] = sdConfig.fileScale.domain()

	const modalityTargets = useModalityJumpTargets(fileStart, fileEnd, buildModalityTargetsForEEG)

	function renderConfigureModal() {
		if (!selectedLayout) {
			console.error("Couldn't render the modal because a layout was not selected")
			return
		}

		setUneditedLayoutGroup(selectedLayoutGroup)
		setEditedLayoutGroup(selectedLayoutGroup)
		createModal(<ConfigureSDDetectionModal windowId={props.windowId} layoutId={selectedLayout.id} />)
	}

	const visualizationSpecificSettings = (<>
		<DataReviewEEGMontageDropdown 
			label={"Montage"}
			currentMontage={props.montage}
			updateCurrentMontage={montage => setSDConfig(previous => ({ ...previous, montage }))}
		/>

		{environmentVariables.FEATURE_FLAG_SD_DETECTION && (
			<MobergDropdown
				label="Auto-SD-detection"
				selectedValue={sdConfig.autoDetectionEnabled}
				options={[
					{ label: "On", value: true },
					{ label: "Off", value: false }
				]}
				onChange={autoDetectionEnabled => setSDConfig(previous => ({...previous, autoDetectionEnabled }))}
			/>
		)}
	</>)

	return (
		<VisualizationToolbar
			windowId={props.windowId}
			layoutId={selectedLayout?.id as string}
			atomValue={sdConfig}
			setAtom={setSDConfig}
			atomKey={atom.key}
			renderConfigureModal={renderConfigureModal}
			viewDuration={viewDuration}
			timePresetOptions={SD_DETECTION_WINDOW_TIME_PRESETS}
			visualizationSpecificSettings={visualizationSpecificSettings}
			modalityTargets={modalityTargets}
		/>
	)
}
